import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Shield, FileText, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const COMPANY = {
  name: "Marketing de Inteligencia Artificial S de RL de CV",
  short: "Marketing de IA S de RL de CV",
  address: "Av. Américas 1545, pisos 20, 21 y 22, Col. Providencia, Guadalajara, Jalisco, México.",
  legal_rep: "Ricardo Zermeño Mejía",
  brand: "HOLA RADAR",
};
const YEAR = new Date().getFullYear();

const DOCS = {
  privacy: {
    title: "Aviso de Privacidad",
    icon: Shield,
    body: `
En cumplimiento de la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), ${COMPANY.name} (en adelante "la Empresa"), con domicilio en ${COMPANY.address}, informa lo siguiente:

1. RESPONSABLE DEL TRATAMIENTO. La Empresa es responsable del uso, protección y tratamiento de sus datos personales.

2. DATOS RECABADOS. Para los USUARIOS/TRANSEÚNTES, HOLA RADAR opera bajo un modelo anónimo: sólo generamos un identificador aleatorio en tu dispositivo (device_id) y usamos temporalmente la ubicación GPS que tú activas en la app. No pedimos nombre, correo, teléfono ni ningún dato de identificación personal. Para los COMERCIOS, recabamos: nombre del negocio, número de celular o correo electrónico, categoría, dirección y coordenadas GPS del local.

3. FINALIDADES. (a) Usuarios: emparejar tu ubicación con comercios cercanos y entregarte ofertas; (b) Comercios: administrar tu cuenta, cobros y estadísticas; (c) Ambos: cumplir obligaciones fiscales y legales aplicables.

4. TRANSFERENCIAS. No compartimos tus datos con terceros salvo con Stripe Inc. (para el procesamiento de pagos) y OpenAI/Anthropic/Google (para respuestas del agente de IA en formato anonimizado, sin PII).

5. DERECHOS ARCO. Puedes ejercer tus derechos de Acceso, Rectificación, Cancelación u Oposición enviando correo a privacidad@holaradar.mx, indicando: (i) nombre y contacto; (ii) documento oficial; (iii) descripción clara del derecho a ejercer.

6. USO DE COOKIES Y ALMACENAMIENTO LOCAL. Utilizamos localStorage y cookies estrictamente técnicas para mantener tu sesión anónima o de comercio. Puedes borrarlas desde tu navegador.

7. CAMBIOS. La Empresa se reserva el derecho de actualizar este aviso. Publicaremos cualquier cambio en esta misma página. Última actualización: ${new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}.
`.trim(),
  },

  "terms-users": {
    title: "Términos y Condiciones — Usuarios",
    icon: FileText,
    body: `
Estos términos rigen el uso de HOLA RADAR por parte de USUARIOS/TRANSEÚNTES.

1. EL SERVICIO. HOLA RADAR es una Web App Progresiva (PWA) que, previa autorización del GPS de tu dispositivo, te notifica ofertas y regalos de comercios afiliados que se encuentran a menos de 50 metros de tu ubicación en tiempo real.

2. GRATUIDAD PARA EL USUARIO. El servicio es y será GRATUITO para los usuarios finales. No pagarás por escanear el código QR, activar tu radar, recibir promociones ni conversar con la IA.

3. USO ANÓNIMO. No requerimos que crees una cuenta ni proporciones datos personales. Sólo generamos un identificador aleatorio en tu dispositivo. Puedes borrarlo en cualquier momento eliminando los datos del sitio en tu navegador.

4. CONTROL DEL GPS. Tú puedes activar o desactivar el radar (permiso GPS) en cualquier momento desde el interruptor visible en la app. Si lo apagas, dejarás de recibir ofertas y no se registrará ninguna ubicación.

5. RESPONSABILIDAD DE LAS OFERTAS. Las ofertas, descuentos y regalos son responsabilidad exclusiva del comercio que los publica. HOLA RADAR actúa únicamente como intermediario tecnológico. Ante cualquier reclamación relacionada con la oferta, deberás dirigirte directamente al comercio.

6. USO PROHIBIDO. Está prohibido usar la app para actividades ilegales, spamear a los comercios afiliados, hacer ingeniería inversa, extraer datos masivamente, o cualquier acción que dañe la operación del servicio.

7. LIMITACIÓN DE RESPONSABILIDAD. La Empresa no garantiza la disponibilidad ininterrumpida del servicio. No somos responsables por fallas del GPS del dispositivo, errores del navegador, o interrupciones de terceros (servidor, red, Stripe).

8. LEY APLICABLE. Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia se someterá a los tribunales competentes de Guadalajara, Jalisco.

9. REPRESENTANTE LEGAL. ${COMPANY.legal_rep}, en representación de ${COMPANY.name}.
`.trim(),
  },

  "terms-merchants": {
    title: "Términos y Condiciones — Comercios",
    icon: Building2,
    body: `
Estos términos rigen la relación entre HOLA RADAR y los COMERCIOS afiliados.

1. LA PLATAFORMA. HOLA RADAR provee al comercio una infraestructura de radar GPS, chatbot de IA y panel de administración para captar clientes que caminan a menos de 50 metros del local.

2. SUSCRIPCIÓN. HOLA RADAR ofrece dos paquetes prepago: (a) PAQUETE CUPONES MARKETING: $299 MXN + IVA por cada 30 días (QR personalizado, base de datos propia, cupones con diseño IA, campañas a la base propia y a toda la población, Cuponera, Cupón de Oro y entrega de boletos del Sorteo Mensual); (b) PAQUETE HOLA RADAR: $990 MXN + IVA por cada 30 días (toda la plataforma: radar GPS, ofertas, Aura IA, diseño IA, MANADA, Atínale al Precio, catálogo, más todo el Paquete Cupones Marketing). El Sorteo Mensual de electrodomésticos es organizado y financiado por MARKETING DE INTELIGENCIA ARTIFICIAL; los comercios únicamente entregan boletos digitales y no pagan ni entregan premios. También se puede pagar el equivalente en USD vía Stripe. La suscripción es prepago y no se renueva automáticamente vía Stripe (para evitar cargos sorpresa). Sin contrato forzoso: el comercio puede cancelar en cualquier momento y ya no será cobrado en el siguiente periodo. Los pagos pueden hacerse por: (a) tarjeta de crédito/débito vía Stripe; (b) OXXO/CoDi si tu cuenta Stripe MX lo tiene habilitado; (c) transferencia bancaria SPEI a la cuenta BBVA registrada.

3. CONTENIDO DEL COMERCIO. El comercio es único responsable del contenido de sus ofertas, promociones y regalos. Se compromete a: (a) entregar realmente lo prometido; (b) no publicar contenido engañoso, ilegal o discriminatorio; (c) mantener actualizada la información de contacto y GPS.

4. GARANTÍA DE ENTREGA. El comercio se obliga a honrar toda oferta activa que un usuario haya reclamado dentro de la app, siempre y cuando el usuario esté físicamente presente en el local dentro del horario configurado.

5. USO DEL LOGO Y MARCA. El comercio autoriza a HOLA RADAR a usar su nombre y logo con fines exclusivamente promocionales dentro de la plataforma. La marca HOLA RADAR es propiedad de ${COMPANY.name} y no puede ser reproducida sin autorización.

6. CANCELACIÓN. El comercio puede cancelar en cualquier momento. No hay reembolso proporcional del periodo pagado. Al cancelar, sus ofertas se pausan y su comercio deja de aparecer en el radar de los usuarios.

7. FACTURACIÓN CFDI. Los pagos en pesos (transferencia bancaria SPEI y OXXO) se pueden facturar bajo régimen fiscal aplicable. Solicita CFDI a facturacion@holaradar.mx dentro del mismo mes del pago.

8. RESPONSABILIDAD LIMITADA. HOLA RADAR no se hace responsable por lucro cesante, daños indirectos o consecuenciales. La responsabilidad máxima de la Empresa se limita al monto pagado por el comercio en los últimos 30 días.

9. TERMINACIÓN. Podemos suspender un comercio con o sin previo aviso si: (a) no cumple con estos términos; (b) recibimos quejas fundadas de usuarios; (c) hay indicios de fraude, uso indebido o incumplimiento fiscal.

10. LEY APLICABLE. Estos términos se rigen por las leyes de los Estados Unidos Mexicanos. Jurisdicción exclusiva: tribunales de Guadalajara, Jalisco.

11. REPRESENTANTE LEGAL. ${COMPANY.legal_rep}, en representación de ${COMPANY.name}.
`.trim(),
  },

  nda: {
    title: "Acuerdo de Confidencialidad",
    icon: Lock,
    body: `
ACUERDO DE CONFIDENCIALIDAD ("NDA")

Entre ${COMPANY.name}, representada en este acto por ${COMPANY.legal_rep} en su calidad de responsable ("HOLA RADAR"), y la persona física o moral que acceda al presente ("La Parte Receptora"), quienes en conjunto se denominarán "Las Partes", celebran el presente Acuerdo de Confidencialidad bajo las siguientes cláusulas:

CLÁUSULA PRIMERA. OBJETO. Regular el intercambio de información confidencial, técnica, comercial, financiera y estratégica que HOLA RADAR entregue a La Parte Receptora con motivo de reuniones, negociaciones, alianzas, integraciones o desarrollos conjuntos vinculados a la plataforma HOLA RADAR.

CLÁUSULA SEGUNDA. INFORMACIÓN CONFIDENCIAL. Se considerará confidencial toda información, sea escrita, verbal, digital, visual o de cualquier otra naturaleza, incluyendo: (a) el modelo de negocio y estrategias comerciales; (b) el código fuente, arquitecturas de software y prompts de la inteligencia artificial; (c) los datos de comercios, usuarios y matches; (d) los precios, márgenes, estados financieros y proyecciones; (e) los planes de expansión y alianzas.

CLÁUSULA TERCERA. OBLIGACIONES DE LA PARTE RECEPTORA. La Parte Receptora se obliga a: (a) guardar absoluta reserva sobre la Información Confidencial; (b) no divulgarla, publicarla, reproducirla ni utilizarla para fines distintos a los del presente Acuerdo; (c) limitar su acceso al personal estrictamente necesario, quienes deberán asumir las mismas obligaciones; (d) tomar medidas razonables para proteger la información física y digitalmente.

CLÁUSULA CUARTA. EXCLUSIONES. No se considerará confidencial la información que: (a) sea de dominio público al momento de su revelación; (b) La Parte Receptora ya poseyera legítimamente antes de la revelación; (c) sea obtenida legalmente de un tercero sin obligación de confidencialidad; (d) sea desarrollada de forma independiente por La Parte Receptora sin uso de la Información Confidencial.

CLÁUSULA QUINTA. VIGENCIA. El presente Acuerdo tendrá una vigencia de CINCO (5) años contados a partir de la primera revelación, subsistiendo las obligaciones de confidencialidad incluso después de terminada la relación entre Las Partes.

CLÁUSULA SEXTA. RESPONSABLE. El responsable directo de este Acuerdo y del resguardo de la Información Confidencial por parte de HOLA RADAR es ${COMPANY.legal_rep}.

CLÁUSULA SÉPTIMA. INCUMPLIMIENTO. Cualquier violación al presente Acuerdo dará lugar a: (a) la reparación de los daños y perjuicios causados; (b) las sanciones civiles y/o penales que resulten aplicables conforme al Código Penal Federal (art. 210, 211 y 211 bis) y demás legislación mexicana en la materia.

CLÁUSULA OCTAVA. JURISDICCIÓN. Las Partes se someten expresamente a los tribunales competentes de la ciudad de Guadalajara, Jalisco, renunciando a cualquier otro fuero que por razón de su domicilio presente o futuro les pudiera corresponder.

Domicilio de HOLA RADAR: ${COMPANY.address}
`.trim(),
  },
};

export default function LegalPage() {
  const { doc } = useParams();
  const active = DOCS[doc] || DOCS["privacy"];
  const Icon = active.icon;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-black" data-testid="legal-back-home">
            <ArrowLeft className="w-4 h-4" /> Regresar
          </Link>
          <div className="font-heading font-extrabold text-sm">HOLA RADAR · Legal</div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 md:px-10 py-10">
        {/* Nav pills */}
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.entries(DOCS).map(([slug, d]) => (
            <Link key={slug} to={`/legal/${slug}`} data-testid={`legal-tab-${slug}`}>
              <Button
                variant={slug === doc ? "default" : "outline"}
                className={`rounded-full text-xs font-bold ${
                  slug === doc ? "bg-black text-white" : ""
                }`}
              >
                <d.icon className="w-3.5 h-3.5" /> {d.title}
              </Button>
            </Link>
          ))}
        </div>

        <div className="rounded-3xl bg-white border border-neutral-200 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#FF007A]/10 text-[#FF007A] grid place-items-center">
              <Icon className="w-5 h-5" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{active.title}</h1>
          </div>

          <div className="text-sm md:text-[15px] leading-relaxed text-neutral-700 whitespace-pre-wrap" data-testid="legal-body">
            {active.body}
          </div>

          <div className="mt-10 pt-6 border-t border-neutral-200 text-xs text-neutral-500 space-y-1">
            <div><strong className="text-black">Empresa:</strong> {COMPANY.name}</div>
            <div><strong className="text-black">Domicilio:</strong> {COMPANY.address}</div>
            <div><strong className="text-black">Representante legal:</strong> {COMPANY.legal_rep}</div>
            <div className="pt-2">© {YEAR} {COMPANY.brand}. Todos los derechos reservados.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
